'use client';

import { WebshopCategory, WebshopProduct } from '@cldsystems/ws-api-client';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import FullWidthBannerImageSection from '@/components/common/shops/hero-image-section';
import ImageBannerWithTextSection from '@/components/common/shops/hero-section';
import StoreLocationsList from '@/components/common/shops/shops-locations-list';
import ImageTextContainer from '@/components/common/shops/text-image-container';
import { LazyBlockNoteRichTextRenderer as BlockNoteRichTextRenderer } from '@/components/dynamic/blocknote-rich-text-renderer/lazy-blocknote-rich-text-renderer';
import CategoryCarouselSection from '@/components/dynamic/category-carousel-section/category-carousel-section';
import ContactSection from '@/components/dynamic/contact-section/contact-section';
import { CustomHtmlContent } from '@/components/dynamic/custom-html-content/custom-html-content';
import FullBleedHeroSection from '@/components/dynamic/full-bleed-hero-section/full-bleed-hero-section';
import HeroBannerWithCarouselSection from '@/components/dynamic/hero-section/hero-section';
import IconsWithDescription from '@/components/dynamic/icon-with-descriptions/icon-with-descriptions';
import ImageGridSection from '@/components/dynamic/image-grid-section/image-grid-section';
import ImageCardsWithText from '@/components/dynamic/image-with-descriptions/image-with-descriptions';
import { SectionPaddingContainer } from '@/components/dynamic/section-padding-container/section-padding-container';
import VideoHeroSection from '@/components/dynamic/video-hero-section/video-hero-section';
import FeaturedProductsSection from '@/components/landing/item-showcase/featured-products-section';
import GridCard from '@/components/recipes/grid-card/grid-card';
import RecipeCardGrid from '@/components/recipes/recipe-card-grid/recipe-card-grid';
import RecipeHeroBanner from '@/components/recipes/recipe-hero/recipe-hero';
import RecipeIngredients from '@/components/recipes/recipe-ingredients/recipe-ingredients';
import RecipePreparationSteps from '@/components/recipes/recipe-preparation/recipe-preparation';
import SimilarRecipes from '@/components/recipes/similar-recipes/similar-recipes';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { StaticPageSection } from '@/services/get-static-pages';
import { List } from '@/types/common';
import {
  resolveMultilingualValue,
  stripLegacyLocalePrefixFromHref,
} from '@/utils/resolve-multilingual-value';
import { resolveSectionPaddingStyle } from '@/utils/section-padding';
import { normalizeSectionType } from '@/utils/section-type';

type FeatureItem = { icon: string; title: string; description: string };
type ImageItem = { image: string; title: string; description: string };
type TextImageItem = {
  image: string;
  title: string;
  description: string;
  reverse?: boolean | string;
  buttonText?: string;
  buttonHref?: string;
  buttonStartIcon?: string;
  buttonEndIcon?: string;
};
type ImageGridItem = { label: string; src: string; href: string };
type LocationItem = { name: string; address: string };
type LocationsSection = { name: string; items: LocationItem[] };
type TabProductItem = { label?: unknown; productIds?: string[] | Record<string, string[]> };
type CardItem = {
  image: string;
  date?: string;
  title: string;
  description: string;
  href?: string;
  cookingTime?: string;
};

import { GUMLET_PRESETS } from '@/utils/gumlet';
import { getRecipeHeroLcpImage } from '@/utils/recipe-hero-lcp';
import {
  replaceUnavailablePlaceholder,
  resolveStorefrontImage,
} from '@/utils/resolve-storefront-image';

type Props = {
  sections: StaticPageSection[];
  locale: string;
  defaultLocale?: string;
};

const PRIMARY_PAGE_HEADING_SECTION_TYPES = new Set([
  'HERO_BANNER_WITH_CAROUSEL',
  'FULL_BLEED_HERO',
  'VIDEO_HERO',
  'BLOG_HERO',
  'RECIPE_HERO_BANNER',
]);

const getPrimaryPageHeadingSectionId = (sections: StaticPageSection[]) =>
  sections.find(section =>
    PRIMARY_PAGE_HEADING_SECTION_TYPES.has(normalizeSectionType(section.type)),
  )?.id;

const SectionRenderer = ({ sections, locale, defaultLocale }: Props) => {
  const r = (value: unknown) => resolveMultilingualValue(value, locale, defaultLocale);
  const primaryPageHeadingSectionId = getPrimaryPageHeadingSectionId(sections);

  return (
    <>
      {sections.map(section => (
        <SectionContent
          key={section.id}
          section={section}
          r={r}
          locale={locale}
          isPrimaryPageHeading={section.id === primaryPageHeadingSectionId}
        />
      ))}
    </>
  );
};

const SectionContent = ({
  section,
  r,
  locale,
  isPrimaryPageHeading,
}: {
  section: StaticPageSection;
  r: (value: unknown) => string;
  locale: string;
  isPrimaryPageHeading: boolean;
}) => {
  const resolveThemeColor = useResolveThemeColor();
  const { t } = useTranslation();
  const appContext = useAppContext<{
    categories?: List<WebshopCategory>;
    allProductsByLocale?: List<WebshopProduct>;
  }>();
  const categories = appContext.categories?.items ?? [];
  const products = appContext.allProductsByLocale?.items ?? [];
  const sectionPadding = (type: string, style?: Record<string, unknown> | null) =>
    resolveSectionPaddingStyle(type, style ?? undefined);
  const headingLevel = isPrimaryPageHeading ? 1 : 2;

  // Normalize legacy type strings from older saved pages to canonical types.
  switch (normalizeSectionType(section.type)) {
    case 'HERO_BANNER_WITH_CAROUSEL': {
      const { title, subtitle, button, media, layout } = section.properties;
      const heroSectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const images = ((media?.images as string[]) ?? [])
        .map(replaceUnavailablePlaceholder)
        .filter(img => img.trim() !== '');
      const mobileGap =
        layout?.mobileGap !== undefined && layout?.mobileGap !== ''
          ? Number(layout.mobileGap)
          : undefined;
      return (
        <HeroBannerWithCarouselSection
          headingLevel={headingLevel}
          mobileGap={Number.isFinite(mobileGap) ? mobileGap : undefined}
          title={{
            text: r(title?.text),
            color: String(title?.color ?? 'foreground'),
            fontSize: `${title?.fontSize ?? 32}px`,
            fontWeight: String(title?.fontWeight ?? 'bold'),
          }}
          subtitle={{
            text: r(subtitle?.text),
            color: String(subtitle?.color ?? 'default-500'),
            fontSize: `${subtitle?.fontSize ?? 18}px`,
            fontWeight: String(subtitle?.fontWeight ?? 'normal'),
          }}
          button={{
            text: r(button?.text) || 'Click me',
            href: stripLegacyLocalePrefixFromHref(r(button?.href) || '/') || '/',
            startIcon: String(button?.startIcon ?? ''),
            endIcon: String(button?.endIcon ?? ''),
          }}
          images={images.filter(img => img.trim() !== '')}
          background={{
            type: String(section.properties.background?.type ?? 'COLOR') as
              | 'IMAGE'
              | 'VIDEO'
              | 'COLOR',
            value: String(section.properties.background?.value ?? 'white'),
            imageValue: section.properties.background?.imageValue
              ? String(section.properties.background.imageValue)
              : undefined,
            gradient: section.properties.background?.gradient
              ? String(section.properties.background.gradient)
              : undefined,
          }}
          contentPaddingStyle={sectionPadding('HERO_BANNER_WITH_CAROUSEL', heroSectionStyle)}
        />
      );
    }
    case 'FULL_BLEED_HERO': {
      const { label, title, subtitle, body, button, media, overlay } = section.properties;
      const heroSectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const images = ((media?.images as string[]) ?? [])
        .map(replaceUnavailablePlaceholder)
        .filter(img => img.trim() !== '');
      const minHeightRaw =
        heroSectionStyle?.minHeight !== undefined && heroSectionStyle?.minHeight !== ''
          ? Number(heroSectionStyle.minHeight)
          : undefined;
      const minHeight = Number.isFinite(minHeightRaw) ? minHeightRaw : 640;
      const labelText = r(label?.text);
      return (
        <FullBleedHeroSection
          headingLevel={headingLevel}
          minHeight={minHeight}
          label={
            labelText
              ? {
                  text: labelText,
                  color: String(label?.color ?? 'foreground'),
                  fontSize: `${label?.fontSize ?? 12}px`,
                  fontWeight: String(label?.fontWeight ?? 'bold'),
                }
              : undefined
          }
          title={{
            text: r(title?.text),
            color: String(title?.color ?? 'content1'),
            fontSize: `${title?.fontSize ?? 48}px`,
            fontWeight: String(title?.fontWeight ?? 'bold'),
          }}
          subtitle={{
            text: r(subtitle?.text),
            color: String(subtitle?.color ?? 'foreground'),
            fontSize: `${subtitle?.fontSize ?? 20}px`,
            fontWeight: String(subtitle?.fontWeight ?? 'normal'),
          }}
          body={{
            text: r(body?.text),
            color: String(body?.color ?? 'foreground'),
            fontSize: `${body?.fontSize ?? 14}px`,
            fontWeight: String(body?.fontWeight ?? 'normal'),
          }}
          button={{
            text: r(button?.text) || 'shop now',
            href: stripLegacyLocalePrefixFromHref(r(button?.href) || '/') || '/',
            startIcon: String(button?.startIcon ?? ''),
            endIcon: String(button?.endIcon ?? ''),
          }}
          images={images.length > 0 ? images : []}
          overlayGradient={overlay?.gradient ? String(overlay.gradient) : undefined}
          contentPaddingStyle={sectionPadding('FULL_BLEED_HERO', heroSectionStyle)}
        />
      );
    }
    case 'VIDEO_HERO': {
      const { label, title, subtitle, body, button, media, overlay } = section.properties;
      const heroSectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const minHeightRaw =
        heroSectionStyle?.minHeight !== undefined && heroSectionStyle?.minHeight !== ''
          ? Number(heroSectionStyle.minHeight)
          : undefined;
      const minHeight = Number.isFinite(minHeightRaw) ? minHeightRaw : 640;
      const labelText = r(label?.text);
      const videoSrc = typeof media?.videoSrc === 'string' ? media.videoSrc.trim() : '';
      const buttonEnabled = button?.enabled !== false && button?.enabled !== 'false';
      return (
        <VideoHeroSection
          headingLevel={headingLevel}
          minHeight={minHeight}
          label={
            labelText
              ? {
                  text: labelText,
                  color: String(label?.color ?? 'content1'),
                  fontSize: `${label?.fontSize ?? 12}px`,
                  fontWeight: String(label?.fontWeight ?? 'bold'),
                }
              : undefined
          }
          title={{
            text: r(title?.text),
            color: String(title?.color ?? 'content1'),
            fontSize: `${title?.fontSize ?? 56}px`,
            fontWeight: String(title?.fontWeight ?? 'bold'),
          }}
          subtitle={{
            text: r(subtitle?.text),
            color: String(subtitle?.color ?? 'content1'),
            fontSize: `${subtitle?.fontSize ?? 20}px`,
            fontWeight: String(subtitle?.fontWeight ?? 'normal'),
          }}
          body={{
            text: r(body?.text),
            color: String(body?.color ?? 'content1'),
            fontSize: `${body?.fontSize ?? 14}px`,
            fontWeight: String(body?.fontWeight ?? 'normal'),
          }}
          button={
            buttonEnabled
              ? {
                  text: r(button?.text) || 'Explore',
                  href: stripLegacyLocalePrefixFromHref(r(button?.href) || '/') || '/',
                  startIcon: String(button?.startIcon ?? ''),
                  endIcon: String(button?.endIcon ?? ''),
                }
              : undefined
          }
          videoSrc={videoSrc || undefined}
          posterImage={resolveStorefrontImage(
            media?.posterImage,
            undefined,
            GUMLET_PRESETS.fullBleedHero,
          )}
          overlayGradient={overlay?.gradient ? String(overlay.gradient) : undefined}
          contentPaddingStyle={sectionPadding('VIDEO_HERO', heroSectionStyle)}
        />
      );
    }
    case 'ICONS_WITH_DESCRIPTION': {
      const { section: sectionStyle, features, style } = section.properties;
      const sectionProps = sectionStyle as { background?: string } | undefined;
      const rawItems = (features?.items as FeatureItem[]) ?? [];
      const items = rawItems.map(item => ({
        ...item,
        title: r(item.title),
        description: r(item.description),
      }));
      return (
        <div
          className="w-full"
          style={
            sectionProps?.background
              ? { backgroundColor: resolveThemeColor(sectionProps.background) }
              : undefined
          }
        >
          <IconsWithDescription
            features={items}
            featureCircleBackground={resolveThemeColor(
              String(style?.featureCircleBackground ?? '#3b82f6'),
            )}
            iconSize={(style?.iconSize as SizeProp) ?? 'xl'}
            titleColor={style?.titleColor ? resolveThemeColor(String(style.titleColor)) : undefined}
            descriptionColor={
              style?.descriptionColor
                ? resolveThemeColor(String(style.descriptionColor))
                : undefined
            }
            paddingStyle={sectionPadding(
              'ICONS_WITH_DESCRIPTION',
              sectionProps as Record<string, unknown>,
            )}
          />
        </div>
      );
    }
    case 'IMAGE_CARDS_WITH_TEXT': {
      const { section: sectionStyle, content, style } = section.properties;
      const sectionProps = sectionStyle as
        | {
            background?: string;
            titleColor?: string;
            descriptionColor?: string;
            layout?: 'vertical' | 'horizontal';
            imageWidth?: number;
            imageHeight?: number;
          }
        | undefined;
      const legacyStyle = style as { titleColor?: string; descriptionColor?: string } | undefined;
      const rawItems = (content?.items as ImageItem[]) ?? [];
      const items = rawItems.map(item => ({
        ...item,
        image: resolveStorefrontImage(item.image, undefined, GUMLET_PRESETS.thumbnail),
        title: r(item.title),
        description: r(item.description),
      }));
      const titleColorValue = sectionProps?.titleColor ?? legacyStyle?.titleColor;
      const descriptionColorValue = sectionProps?.descriptionColor ?? legacyStyle?.descriptionColor;
      const imageWidth = Number(sectionProps?.imageWidth);
      const imageHeight = Number(sectionProps?.imageHeight);
      return (
        <div
          className="w-full overflow-hidden"
          style={
            sectionProps?.background
              ? { backgroundColor: resolveThemeColor(sectionProps.background) }
              : undefined
          }
        >
          <ImageCardsWithText
            items={items}
            titleColor={titleColorValue ? resolveThemeColor(String(titleColorValue)) : undefined}
            descriptionColor={
              descriptionColorValue ? resolveThemeColor(String(descriptionColorValue)) : undefined
            }
            layout={sectionProps?.layout === 'horizontal' ? 'horizontal' : 'vertical'}
            imageWidth={imageWidth > 0 ? imageWidth : undefined}
            imageHeight={imageHeight > 0 ? imageHeight : undefined}
            paddingStyle={sectionPadding(
              'IMAGE_CARDS_WITH_TEXT',
              sectionProps as Record<string, unknown>,
            )}
          />
        </div>
      );
    }
    case 'IMAGE_BANNER_WITH_TEXT': {
      const sectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const hero =
        (section.properties.hero as {
          backgroundImage?: string;
          title?: unknown;
          subtitle?: unknown;
        }) ?? {};
      return (
        <ImageBannerWithTextSection
          backgroundImage={resolveStorefrontImage(
            hero.backgroundImage,
            undefined,
            GUMLET_PRESETS.heroBackground,
          )}
          title={r(hero.title) || t?.common.shops || ''}
          subtitle={r(hero.subtitle) || ''}
          paddingStyle={sectionPadding('IMAGE_BANNER_WITH_TEXT', sectionStyle)}
        />
      );
    }
    case 'FULL_WIDTH_BANNER_IMAGE': {
      const sectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const hero = (section.properties.hero as { backgroundImage?: string }) ?? {};
      return (
        <FullWidthBannerImageSection
          backgroundImage={resolveStorefrontImage(
            hero.backgroundImage,
            undefined,
            GUMLET_PRESETS.heroBackground,
          )}
          paddingStyle={sectionPadding('FULL_WIDTH_BANNER_IMAGE', sectionStyle)}
        />
      );
    }
    case 'BLOG_HERO': {
      const sectionStyle = section.properties.section as Record<string, unknown> | undefined;
      const hero = (section.properties.hero as { backgroundImage?: string; title?: unknown }) ?? {};
      const paddingStyle = sectionPadding('BLOG_HERO', sectionStyle);
      return (
        <div
          className="relative m-0 flex w-full items-center justify-center overflow-hidden rounded-b-4xl bg-cover bg-center"
          style={{
            backgroundImage: `url(${resolveStorefrontImage(hero.backgroundImage, undefined, GUMLET_PRESETS.heroBackground)})`,
            ...paddingStyle,
          }}
        >
          <div className="absolute inset-0 bg-white opacity-70" />
          <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
            {isPrimaryPageHeading ? (
              <h1 className="text-3xl font-bold text-black @sm:text-4xl @md:text-5xl">
                {r(hero.title) || 'Blog title'}
              </h1>
            ) : (
              <h2 className="text-3xl font-bold text-black @sm:text-4xl @md:text-5xl">
                {r(hero.title) || 'Blog title'}
              </h2>
            )}
          </div>
        </div>
      );
    }
    case 'STORE_LOCATIONS': {
      const sectionStyle = section.properties.section as
        | {
            title?: unknown;
            background?: string;
            regionColor?: string;
            locationColor?: string;
            addressColor?: string;
          }
        | undefined;
      const content = section.properties.content as { sections?: LocationsSection[] } | undefined;
      const rawSections = content?.sections ?? [];
      const sectionsList = rawSections.map(sec => ({
        ...sec,
        name: r(sec.name),
        items: (sec.items ?? []).map(loc => ({
          ...loc,
          name: r(loc.name),
          address: r(loc.address),
        })),
      }));
      return (
        <StoreLocationsList
          title={r(sectionStyle?.title) || ''}
          background={String(sectionStyle?.background ?? '#ffffff')}
          regionColor={String(sectionStyle?.regionColor ?? '#dc2626')}
          locationColor={String(sectionStyle?.locationColor ?? '#000000')}
          addressColor={String(sectionStyle?.addressColor ?? '#000000')}
          sections={sectionsList}
          paddingStyle={sectionPadding('STORE_LOCATIONS', sectionStyle as Record<string, unknown>)}
        />
      );
    }
    case 'IMAGE_TEXT_CONTAINER': {
      const { section: sectionStyle, content } = section.properties;
      const sectionProps = sectionStyle as
        | { background?: string; titleColor?: string; descriptionColor?: string }
        | undefined;
      const rawItems = (content?.items as TextImageItem[]) ?? [];
      const titleColor = sectionProps?.titleColor
        ? resolveThemeColor(String(sectionProps.titleColor))
        : undefined;
      const descriptionColor = sectionProps?.descriptionColor
        ? resolveThemeColor(String(sectionProps.descriptionColor))
        : undefined;
      return (
        <div
          className="w-full"
          style={
            sectionProps?.background
              ? { backgroundColor: resolveThemeColor(sectionProps.background) }
              : undefined
          }
        >
          <SectionPaddingContainer
            sectionType="IMAGE_TEXT_CONTAINER"
            sectionStyle={sectionProps as Record<string, unknown>}
          >
            {rawItems.map((item, index) => (
              <ImageTextContainer
                key={index}
                image={resolveStorefrontImage(item.image, undefined, GUMLET_PRESETS.thumbnail)}
                title={r(item.title)}
                description={r(item.description)}
                reverse={item.reverse === true || item.reverse === 'true'}
                titleColor={titleColor}
                descriptionColor={descriptionColor}
                button={{
                  text: item.buttonText ? r(item.buttonText) : '',
                  href: item.buttonHref
                    ? stripLegacyLocalePrefixFromHref(r(item.buttonHref)) || '/'
                    : '/',
                  startIcon: item.buttonStartIcon ? String(item.buttonStartIcon) : undefined,
                  endIcon: item.buttonEndIcon ? String(item.buttonEndIcon) : undefined,
                }}
              />
            ))}
          </SectionPaddingContainer>
        </div>
      );
    }
    case 'RICH_TEXT': {
      const sectionStyle = section.properties.section as
        | { height?: number; background?: string; hideLoadingSkeleton?: boolean }
        | undefined;
      const content = section.properties.content as { value?: unknown } | undefined;
      const richTextValue = r(content?.value);

      return (
        <div
          className="w-full"
          style={
            sectionStyle?.background
              ? { backgroundColor: resolveThemeColor(sectionStyle.background) }
              : undefined
          }
        >
          <SectionPaddingContainer
            sectionType="RICH_TEXT"
            sectionStyle={sectionStyle as Record<string, unknown>}
          >
            <BlockNoteRichTextRenderer
              value={richTextValue}
              height={sectionStyle?.height}
              hideSkeleton={sectionStyle?.hideLoadingSkeleton === true}
            />
          </SectionPaddingContainer>
        </div>
      );
    }
    case 'CUSTOM_HTML': {
      const sectionStyle = section.properties.section as { background?: string } | undefined;
      const content = section.properties.content as { html?: unknown } | undefined;
      const consent = section.properties.consent as { requiredConsent?: string } | undefined;
      const html = r(content?.html);

      return (
        <div
          className="w-full"
          style={
            sectionStyle?.background
              ? { backgroundColor: resolveThemeColor(sectionStyle.background) }
              : undefined
          }
        >
          <SectionPaddingContainer
            sectionType="CUSTOM_HTML"
            sectionStyle={sectionStyle as Record<string, unknown>}
          >
            <CustomHtmlContent html={html} requiredConsent={consent?.requiredConsent} />
          </SectionPaddingContainer>
        </div>
      );
    }
    case 'IMAGE_GRID': {
      const { section: sectionStyle, title: titleStyle, items: itemsGroup } = section.properties;
      const sectionProps = sectionStyle as { height?: string; background?: string } | undefined;
      const titleProps = titleStyle as
        | { text?: unknown; color?: string; fontSize?: number; fontWeight?: string }
        | undefined;
      const rawGridItems = (itemsGroup?.items as ImageGridItem[]) ?? [];
      const gridItems = rawGridItems.map(item => ({ ...item, label: r(item.label) }));
      return (
        <ImageGridSection
          height={String(sectionProps?.height ?? '400px')}
          background={String(sectionProps?.background ?? '#000000')}
          title={{
            text: r(titleProps?.text),
            color: String(titleProps?.color ?? 'content1'),
            fontSize: `${titleProps?.fontSize ?? 24}px`,
            fontWeight: String(titleProps?.fontWeight ?? 'bold'),
          }}
          items={gridItems}
          paddingStyle={sectionPadding('IMAGE_GRID', sectionProps as Record<string, unknown>)}
        />
      );
    }
    case 'FEATURE_ICON_GRID': {
      const { background, featureCircleBackground, features } = section.properties as unknown as {
        background: string;
        featureCircleBackground: string;
        features: FeatureItem[];
      };
      const sectionStyle = section.properties.section as Record<string, unknown> | undefined;
      return (
        <div style={{ background }}>
          <IconsWithDescription
            features={features}
            featureCircleBackground={featureCircleBackground}
            paddingStyle={sectionPadding('FEATURE_ICON_GRID', sectionStyle)}
          />
        </div>
      );
    }
    case 'CATEGORY_GRID': {
      const sectionStyle = section.properties.section as
        | { height?: string; background?: string }
        | undefined;
      const titleStyle = section.properties.title as
        | { text?: unknown; color?: string; fontSize?: number; fontWeight?: string }
        | undefined;
      const rawCategoryIds =
        (section.properties.items as { items?: string[] | Record<string, string[]> } | undefined)
          ?.items ?? [];
      const categoryIds = Array.isArray(rawCategoryIds)
        ? rawCategoryIds
        : (rawCategoryIds[locale] ?? []);

      const byId = new Map(categories.map(category => [category.id, category]));
      const byName = new Map(categories.map(category => [category.name, category]));

      const gridItems: ImageGridItem[] = categoryIds
        .map(idOrName => byId.get(idOrName) ?? byName.get(idOrName))
        .filter((category): category is NonNullable<typeof category> => category != null)
        .map(category => ({
          label: category.name,
          src: resolveStorefrontImage(
            (category.meta as { thumbnail?: string })?.thumbnail,
            undefined,
            GUMLET_PRESETS.categoryCard,
          ),
          href: `/products/${(category.meta as { url?: string })?.url ?? ''}`,
        }));

      return (
        <ImageGridSection
          height={String(sectionStyle?.height ?? '400px')}
          background={String(sectionStyle?.background ?? '#000000')}
          title={{
            text: r(titleStyle?.text),
            color: String(titleStyle?.color ?? 'content1'),
            fontSize: `${titleStyle?.fontSize ?? 24}px`,
            fontWeight: String(titleStyle?.fontWeight ?? 'bold'),
          }}
          items={gridItems}
          paddingStyle={sectionPadding('CATEGORY_GRID', sectionStyle as Record<string, unknown>)}
        />
      );
    }
    case 'CATEGORY_CAROUSEL': {
      const sectionStyle = section.properties.section as
        | { height?: string; background?: string }
        | undefined;
      const titleStyle = section.properties.title as
        | {
            text?: unknown;
            color?: string;
            fontSize?: number;
            fontWeight?: string;
            arrowColor?: string;
          }
        | undefined;
      const rawCategoryIds =
        (section.properties.items as { items?: string[] | Record<string, string[]> } | undefined)
          ?.items ?? [];
      const categoryIds = Array.isArray(rawCategoryIds)
        ? rawCategoryIds
        : (rawCategoryIds[locale] ?? []);

      const byId = new Map(categories.map(category => [category.id, category]));
      const byName = new Map(categories.map(category => [category.name, category]));

      const carouselItems: ImageGridItem[] = categoryIds
        .map(idOrName => byId.get(idOrName) ?? byName.get(idOrName))
        .filter((category): category is NonNullable<typeof category> => category != null)
        .map(category => ({
          label: category.name,
          src: resolveStorefrontImage(
            (category.meta as { thumbnail?: string })?.thumbnail,
            undefined,
            GUMLET_PRESETS.categoryCard,
          ),
          href: `/products/${(category.meta as { url?: string })?.url ?? ''}`,
        }));

      return (
        <CategoryCarouselSection
          height={String(sectionStyle?.height ?? '400px')}
          background={String(sectionStyle?.background ?? '#000000')}
          title={{
            text: r(titleStyle?.text),
            color: String(titleStyle?.color ?? 'content1'),
            fontSize: `${titleStyle?.fontSize ?? 24}px`,
            fontWeight: String(titleStyle?.fontWeight ?? 'bold'),
          }}
          arrowColor={String(titleStyle?.arrowColor ?? titleStyle?.color ?? 'content1')}
          items={carouselItems}
          paddingStyle={sectionPadding(
            'CATEGORY_CAROUSEL',
            sectionStyle as Record<string, unknown>,
          )}
        />
      );
    }
    case 'PRODUCT_SHOWCASE': {
      const sectionStyle = section.properties.section as { background?: string } | undefined;
      const title = section.properties.title as {
        text?: unknown;
        color?: string;
        fontSize?: number;
        fontWeight?: string;
      };
      const tabStyle = section.properties.tabStyle as
        | { background?: string; textColor?: string }
        | undefined;
      const tabsItems =
        (section.properties.tabs as { items?: TabProductItem[] } | undefined)?.items ?? [];

      const tabsWithIds = tabsItems.map(tab => {
        const raw = tab?.productIds;
        let ids: string[];
        if (Array.isArray(raw)) {
          ids = raw.filter(Boolean);
        } else if (raw && typeof raw === 'object') {
          ids = ((raw as Record<string, string[]>)[locale] ?? []).filter(Boolean);
        } else {
          ids = [];
        }
        return { title: r(tab?.label ?? ''), productIds: ids };
      });

      const productById = new Map(products.map(product => [product.id, product]));
      const featuredProducts = tabsWithIds.map(({ title: tabTitle, productIds }) => ({
        title: tabTitle,
        products: productIds.map(id => productById.get(id) ?? null),
      }));

      return (
        <FeaturedProductsSection
          featuredProducts={featuredProducts}
          sectionTitle={{
            text: r(title?.text) || 'Produse',
            color: String(title?.color ?? 'foreground'),
            fontSize: `${title?.fontSize ?? 24}px`,
            fontWeight: String(title?.fontWeight ?? 'bold'),
          }}
          background={sectionStyle?.background}
          tabsStyle={tabStyle}
          paddingStyle={sectionPadding('PRODUCT_SHOWCASE', sectionStyle as Record<string, unknown>)}
        />
      );
    }
    case 'CONTACT': {
      const { section: sectionStyle, content, style } = section.properties;
      const sectionProps = sectionStyle as { background?: string } | undefined;
      return (
        <div
          className="w-full"
          style={
            sectionProps?.background
              ? { backgroundColor: resolveThemeColor(sectionProps.background) }
              : undefined
          }
        >
          <ContactSection
            title={r(content?.title)}
            description={r(content?.description)}
            email={String(content?.email ?? '')}
            submitLabel={content?.submitLabel ? r(content.submitLabel) : undefined}
            mapEmbedUrl={String(content?.mapEmbedUrl ?? '')}
            titleColor={style?.titleColor ? resolveThemeColor(String(style.titleColor)) : undefined}
            descriptionColor={
              style?.descriptionColor
                ? resolveThemeColor(String(style.descriptionColor))
                : undefined
            }
            labelColor={style?.labelColor ? resolveThemeColor(String(style.labelColor)) : undefined}
            paddingStyle={sectionPadding('CONTACT', sectionProps as Record<string, unknown>)}
          />
        </div>
      );
    }
    case 'RECIPE_PREPARATION_STEPS': {
      const sectionStyle = section.properties.section as
        | { background?: string; stepsColor?: string }
        | undefined;
      const content = section.properties.content as { steps?: unknown[] } | undefined;
      const steps = (content?.steps ?? []).map(s => r(s));
      const stepsColor = sectionStyle?.stepsColor
        ? resolveThemeColor(String(sectionStyle.stepsColor))
        : undefined;
      return (
        <div
          className="w-full"
          style={
            sectionStyle?.background
              ? { backgroundColor: resolveThemeColor(sectionStyle.background) }
              : undefined
          }
        >
          <RecipePreparationSteps
            steps={steps}
            stepsColor={stepsColor}
            paddingStyle={sectionPadding(
              'RECIPE_PREPARATION_STEPS',
              sectionStyle as Record<string, unknown>,
            )}
          />
        </div>
      );
    }
    case 'SIMILAR_RECIPES': {
      const content = section.properties.content as { items?: unknown[] } | undefined;
      const rawItems = (content?.items ?? []) as {
        id?: string;
        image?: string;
        title?: unknown;
        description?: string;
        cookingTime?: unknown;
        href?: string;
      }[];
      const recipes = rawItems.map(item => ({
        id: item.id ?? item.href ?? '',
        title: r(item.title),
        description: item.description ?? '',
        image: item.image ?? '',
        href: item.href ?? '/',
        cookingTime: item.cookingTime ? r(item.cookingTime) : undefined,
      }));
      return (
        <SimilarRecipes
          recipes={recipes}
          paddingStyle={sectionPadding(
            'SIMILAR_RECIPES',
            section.properties.section as Record<string, unknown>,
          )}
        />
      );
    }
    case 'RECIPE_CARD_GRID': {
      const items =
        (section.properties.items as unknown as {
          image?: string;
          title?: unknown;
          cookingTime?: unknown;
          richText?: string;
          href?: string;
        }[]) ?? [];
      return (
        <RecipeCardGrid
          items={items.map(item => ({
            image: resolveStorefrontImage(item.image, undefined, GUMLET_PRESETS.gridTile),
            title: r(item.title),
            cookingTime: item.cookingTime ? r(item.cookingTime) : undefined,
            richText: item.richText,
            href: item.href ?? '/',
          }))}
          paddingStyle={sectionPadding(
            'RECIPE_CARD_GRID',
            section.properties.section as Record<string, unknown>,
          )}
        />
      );
    }
    case 'RECIPE_INGREDIENTS': {
      const sectionStyle = section.properties.section as
        | { background?: string; ingredientsColor?: string }
        | undefined;
      const content = section.properties.content as { ingredients?: unknown[] } | undefined;
      const rawIngredients = (content?.ingredients ?? []) as unknown[];
      const ingredients = rawIngredients.map(item => r(item));
      const ingredientsColor = sectionStyle?.ingredientsColor
        ? resolveThemeColor(String(sectionStyle.ingredientsColor))
        : undefined;
      return (
        <div
          className="w-full"
          style={
            sectionStyle?.background
              ? { backgroundColor: resolveThemeColor(sectionStyle.background) }
              : undefined
          }
        >
          <RecipeIngredients
            ingredients={ingredients}
            ingredientsColor={ingredientsColor}
            paddingStyle={sectionPadding(
              'RECIPE_INGREDIENTS',
              sectionStyle as Record<string, unknown>,
            )}
          />
        </div>
      );
    }
    case 'RECIPE_HERO_BANNER': {
      const sectionStyle = section.properties.section as
        | {
            background?: string;
            titleColor?: string;
            cookingTimeColor?: string;
            servingsColor?: string;
            ingredientsColor?: string;
          }
        | undefined;
      const media = section.properties.media as { image?: string } | undefined;
      const content = section.properties.content as
        | { title?: unknown; cookingTime?: unknown; servings?: unknown }
        | undefined;
      const cart = section.properties.cart as
        | { items?: string[] | Record<string, string[]> }
        | undefined;
      const rawCartIds = cart?.items;
      const cartProductIds = Array.isArray(rawCartIds)
        ? rawCartIds.filter(Boolean)
        : ((rawCartIds as Record<string, string[]>)?.[locale] ?? []).filter(Boolean);
      const lcpImage = getRecipeHeroLcpImage(media?.image);
      return (
        <div
          className="w-full"
          style={
            sectionStyle?.background
              ? { backgroundColor: resolveThemeColor(sectionStyle.background) }
              : undefined
          }
        >
          <RecipeHeroBanner
            image={lcpImage.src}
            imageSrcSet={lcpImage.srcSet}
            imageSizes={lcpImage.sizes}
            title={r(content?.title) || 'Recipe Title'}
            cookingTime={r(content?.cookingTime)}
            servings={r(content?.servings)}
            cartProductIds={cartProductIds}
            titleColor={
              sectionStyle?.titleColor
                ? resolveThemeColor(String(sectionStyle.titleColor))
                : undefined
            }
            cookingTimeColor={
              sectionStyle?.cookingTimeColor
                ? resolveThemeColor(String(sectionStyle.cookingTimeColor))
                : undefined
            }
            servingsColor={
              sectionStyle?.servingsColor
                ? resolveThemeColor(String(sectionStyle.servingsColor))
                : undefined
            }
            ingredientsColor={
              sectionStyle?.ingredientsColor
                ? resolveThemeColor(String(sectionStyle.ingredientsColor))
                : undefined
            }
            paddingStyle={sectionPadding(
              'RECIPE_HERO_BANNER',
              sectionStyle as Record<string, unknown>,
            )}
          />
        </div>
      );
    }
    case 'CONTENT_CARD_GRID': {
      const { section: sectionStyle, cards } = section.properties;
      const sectionProps = sectionStyle as { background?: string } | undefined;
      const rawCards = (cards?.items as CardItem[]) ?? [];
      return (
        <div
          className="w-full"
          style={
            sectionProps?.background
              ? { backgroundColor: resolveThemeColor(sectionProps.background) }
              : undefined
          }
        >
          <SectionPaddingContainer
            sectionType="CONTENT_CARD_GRID"
            sectionStyle={sectionProps as Record<string, unknown>}
          >
            <div className="grid grid-cols-1 items-stretch gap-6 @md:grid-cols-2 @md:gap-8 @4xl:grid-cols-4">
              {rawCards.map((card, index) => (
                <div key={index} className="w-full">
                  <GridCard
                    image={resolveStorefrontImage(card.image, undefined, GUMLET_PRESETS.thumbnail)}
                    date={card.date ?? ''}
                    title={r(card.title)}
                    description={r(card.description)}
                    href={card.href ?? '/'}
                    cookingTime={card.cookingTime}
                    locale={locale}
                  />
                </div>
              ))}
            </div>
          </SectionPaddingContainer>
        </div>
      );
    }
    default:
      return null;
  }
};

export default SectionRenderer;
